import type * as go from 'gojs';
import { store } from 'createStore';
import type { EditConnectionPayloadInterface } from '../../store/connections/sagaActions';
import {
  addConnection,
  editConnection,
  removeConnection,
} from '../../store/connections/sagaActions';
import type { PortKeysInterface } from '../../store/connections/types';
import { UnitTypes } from 'unitData/types';
import type { UndoFromPortInterface, UndoToPortInterface } from './types';
import {
  checkIfLinkIsACopy,
  checkIfLinkExist,
  checkIfUnitExist,
  findCorrectFromPortIndex,
  findCorrectToPortIndex,
} from '../utils/unitChecks';

const handleCopiedLinkInsertion = (
  link: go.ObjectData,
  linkKey: string,
  newFromPortKeysMap: Map<string, UndoFromPortInterface[]>,
  newToPortKeysMap: Map<string, UndoToPortInterface[]>,
) => {
  const availableNewFromPortKeys = newFromPortKeysMap.get(link.from);
  const availableNewToPortKeys = newToPortKeysMap.get(link.to);
  const fromPortIndex = findCorrectFromPortIndex(linkKey);
  const toPortIndex = findCorrectToPortIndex(linkKey);
  const fromUnit = checkIfUnitExist(link.from);
  const toUnit = checkIfUnitExist(link.to);
  if (
    availableNewFromPortKeys &&
    availableNewToPortKeys &&
    typeof fromPortIndex !== 'undefined' &&
    typeof toPortIndex !== 'undefined' &&
    typeof fromUnit !== 'undefined' &&
    typeof toUnit !== 'undefined'
  ) {
    let newToPortKey = '';
    let newFromPortKey = '';
    if (toUnit.type === UnitTypes.ADD) {
      newToPortKey = availableNewToPortKeys[toPortIndex - 1].key;
    } else {
      newToPortKey = availableNewToPortKeys[toPortIndex].key;
    }
    if (fromUnit.type === UnitTypes.ADD) {
      newFromPortKey = availableNewFromPortKeys[fromPortIndex].key;
    } else {
      newFromPortKey = availableNewFromPortKeys[fromPortIndex - 1].key;
    }
    const newPortKeys: PortKeysInterface = {
      from: link.from,
      to: link.to,
      fromPort: newFromPortKey,
      toPort: newToPortKey,
    };

    store.dispatch(addConnection(newPortKeys));
  }
};

const handleNewLinkInsertion = (link: go.ObjectData) => {
  const newPortKeys: PortKeysInterface = {
    from: link.from,
    to: link.to,
    fromPort: link.fromPort,
    toPort: link.toPort,
  };
  store.dispatch(addConnection(newPortKeys));
};

export const handleLinkModifications = (modifiedLinkData: go.ObjectData[]) => {
  const arr: EditConnectionPayloadInterface[] = [];
  modifiedLinkData.forEach((link: go.ObjectData) => {
    const connections =
      store.getState().unitConnGroup.present.connectionState.connections;
    const connection = connections.find(
      (connection) => connection.key === link.key,
    );

    if (typeof connection !== 'undefined') {
      if (typeof link.flowRatePosition !== 'undefined') {
        if (
          connection.fromPort === link.fromPort &&
          connection.toPort === link.toPort &&
          connection.to === link.to &&
          connection.from === link.from &&
          connection.flowRatePosition === link.flowRatePosition
        ) {
          return;
        }

        const editConnectionData = {
          portMode: 'linkFromPortId',
          fromPort: link.fromPort,
          toPort: link.toPort,
          to: link.to,
          from: link.from,
          flowRatePosition: link.flowRatePosition,
        };
        arr.push(editConnectionData);
      }
    }
  });
  if (arr.length > 0) {
    store.dispatch(editConnection(arr));
  }
};

export const handleLinkInsertions = (
  insertedLinkKeys: go.Key[],
  modifiedLinkMap: Map<go.Key, go.ObjectData>,
  newFromPortKeysMap: Map<string, UndoFromPortInterface[]>,
  newToPortKeysMap: Map<string, UndoToPortInterface[]>,
) => {
  insertedLinkKeys.forEach((linkKey: go.Key) => {
    const link = modifiedLinkMap.get(linkKey);
    if (linkKey?.toString()) {
      const currentLinkKey = linkKey.toString();

      if (checkIfLinkExist(currentLinkKey)) {
        return;
      }

      if (checkIfLinkIsACopy(currentLinkKey) && link) {
        handleCopiedLinkInsertion(
          link,
          currentLinkKey,
          newFromPortKeysMap,
          newToPortKeysMap,
        );
      } else if (link) {
        handleNewLinkInsertion(link);
      }
    }
  });
};

export const handleLinkRemovals = (
  removedLinkKeys: (go.Key | undefined)[],
  removedNodeKeys: (go.Key | undefined)[] = [],
) => {
  const removedNodeKeySet = new Set(
    removedNodeKeys
      .map((key) => key?.toString())
      .filter((key): key is string => typeof key === 'string'),
  );
  const units = store.getState().unitConnGroup.present.unitState.units;
  const unitKeys = new Set(
    units
      .map((unit) => unit.key)
      .filter((key): key is string => typeof key === 'string'),
  );

  removedLinkKeys.forEach((linkKey: go.Key) => {
    if (typeof linkKey?.toString() !== 'undefined') {
      const currentLinkKey: string = linkKey.toString();
      const existingLink = checkIfLinkExist(currentLinkKey);
      if (typeof existingLink !== 'undefined') {
        if (
          removedNodeKeySet.has(existingLink.from) ||
          removedNodeKeySet.has(existingLink.to) ||
          !unitKeys.has(existingLink.from) ||
          !unitKeys.has(existingLink.to)
        ) {
          return;
        }

        const portKeysToRemove: PortKeysInterface = {
          from: existingLink.from,
          to: existingLink.to,
          fromPort: existingLink.fromPort,
          toPort: existingLink.toPort,
        };
        store.dispatch(removeConnection(portKeysToRemove));
      }
    }
  });
};
